/**
 * RP28 → RP31 — Écran « Réunion de la semaine » (fiche 01 ARC-A1, ARC-A2 ; D14, D15).
 *
 * Un seul écran, utilisable au bureau et sur iPad (cibles tactiles ≥ 44 px) :
 *   1. Présence : ouvrir la réunion du jour ou la prochaine planifiée, avec ses convoqués
 *      (A2, constat 25 ; sinon la créer depuis la précédente),
 *      « Tous présents » puis les exceptions (présent / absent / excusé) ;
 *   2. Actions : revue des actions ouvertes par entreprise — reporter, lever
 *      (validation de l'architecte), ajouter une action en direct ;
 *   3. Compte rendu : rédaction assistée depuis la réunion, sources pré-cochées
 *      (actions revues, observations depuis le dernier CR, présences) ; le CR est
 *      créé par le chemin unique et rattaché à la réunion ;
 *   4. Relecture puis « Valider et diffuser » (dialogue de diffusion existant) ou
 *      « Soumettre à l'architecte » ; la réunion suivante est proposée (+7 jours)
 *      et planifiée au moment de la diffusion ou de la soumission.
 *
 * API : routes/reunion_semaine_routes.py ; relecture et diffusion : routes existantes.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Users, CheckCircle, ListChecks, Robot, PaperPlaneRight, CalendarPlus, Plus,
  ArrowClockwise, PencilSimple, Warning,
} from '@phosphor-icons/react';
import CRCMarkdownRenderer from './CRCMarkdownRenderer';
import SendCRCDialog from './crc/SendCRCDialog';
import { BOUTONS, OBJETS, STATUT_ACTION, STATUT_COMPTE_RENDU, libelleCompteRendu, FONCTIONS } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };

const PRESENCES = [
  { value: 'confirme', label: 'Présent', on: 'bg-[#2D6A4F] text-white border-[#2D6A4F]' },
  { value: 'decline', label: 'Absent', on: 'bg-[#8B3A3A] text-white border-[#8B3A3A]' },
  { value: 'excuse', label: 'Excusé', on: 'bg-[#B8860B] text-white border-[#B8860B]' },
];

const DECISION_LABEL = {
  reportee: (r) => `Reportée au ${dateFr(r.echeance)}`,
  levee: () => 'Levée',
  ajoutee: () => 'Ajoutée en réunion',
  maintenue: () => 'Revue',
};

const BTN = 'min-h-[44px] px-3 border text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50';

function dateFr(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : String(iso);
}

function dansJours(n, base) {
  const d = base ? new Date(`${String(base).slice(0, 10)}T12:00:00`) : new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function Etape({ numero, titre, icon: Icon, children, testid }) {
  return (
    <section className="card-kroquis" data-testid={testid}>
      <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#6B6B6B] mb-3">
        <span className="w-6 h-6 bg-[#2C2C2C] text-white text-xs flex items-center justify-center">{numero}</span>
        <Icon size={16} /> {titre}
      </h3>
      {children}
    </section>
  );
}

export default function ReunionSemaine({ chantierId, evenementId = null, onClose }) {
  const [etat, setEtat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [reportId, setReportId] = useState(null);
  const [reportDate, setReportDate] = useState('');
  const [ajoutPour, setAjoutPour] = useState(null); // nom d'entreprise, '' = nouvelle
  const [ajout, setAjout] = useState({ titre: '', entreprise: '', echeance: '' });
  const [sources, setSources] = useState({ actions_revues: true, observations: true, presences: true });
  const [notes, setNotes] = useState('');
  const [edition, setEdition] = useState(false);
  const [texte, setTexte] = useState('');
  const [prochaineDate, setProchaineDate] = useState('');
  const [diffuser, setDiffuser] = useState(false);

  const reunion = etat?.reunion || null;
  const cr = etat?.compte_rendu || null;
  const droits = etat?.droits || {};
  const eid = reunion?.id;

  const charger = async (id = eid || evenementId) => {
    try {
      const q = id ? `?evenement_id=${id}` : '';
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/reunion-semaine${q}`, opts);
      setEtat(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Réunion indisponible');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(evenementId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [chantierId, evenementId]);
  useEffect(() => { if (cr && !edition) setTexte(cr.texte || ''); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cr?.id, cr?.texte]);
  useEffect(() => {
    const planifiee = etat?.prochaine_reunion?.date_debut;
    const proposee = etat?.prochaine_proposee?.date_debut;
    setProchaineDate(String(planifiee || proposee || '').slice(0, 16));
  }, [etat?.prochaine_reunion?.date_debut, etat?.prochaine_proposee?.date_debut]);

  const appel = async (cle, fn, ok) => {
    setBusy(cle);
    try {
      const res = await fn();
      if (ok) toast.success(ok);
      await charger();
      return res;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action impossible');
      return null;
    } finally {
      setBusy('');
    }
  };

  // ---- 1. Réunion et présence (RP28) ------------------------------------------
  const ouvrir = async () => {
    setBusy('ouvrir');
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/reunion-semaine/ouvrir`, { evenement_id: evenementId }, opts);
      setEtat(data);
      if (data.creee) toast.success('Réunion du jour créée (mêmes participants que la précédente)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Réunion indisponible');
    } finally {
      setBusy('');
    }
  };

  const tousPresents = () => appel('tous', () => axios.post(`${API_URL}/api/chantiers/${chantierId}/reunions/${eid}/presences`, { tous: 'confirme' }, opts));
  const pointer = (index, reponse) => appel(`p${index}`, () => axios.post(`${API_URL}/api/chantiers/${chantierId}/reunions/${eid}/presences`, { invite_index: index, reponse }, opts));

  // ---- 2. Revue des actions (RP29) ---------------------------------------------
  const reporter = (action, echeance) => appel(`r${action.id}`, () => axios.post(
    `${API_URL}/api/chantiers/${chantierId}/reunions/${eid}/actions/${action.id}/reporter`, { echeance }, opts,
  ), `Action reportée au ${dateFr(echeance)}`).then(() => setReportId(null));

  const lever = (action) => appel(`l${action.id}`, () => axios.post(
    `${API_URL}/api/chantiers/${chantierId}/reunions/${eid}/actions/${action.id}/lever`, {}, opts,
  ), 'Levée validée : elle figurera au compte rendu');

  const ajouter = async () => {
    if (!ajout.titre.trim()) { toast.error('Le titre est requis.'); return; }
    const res = await appel('ajout', () => axios.post(`${API_URL}/api/chantiers/${chantierId}/reunions/${eid}/actions`, {
      titre: ajout.titre.trim(), entreprise: ajout.entreprise.trim() || null, echeance: ajout.echeance || null,
    }, opts), 'Action ajoutée');
    if (res) { setAjout({ titre: '', entreprise: '', echeance: '' }); setAjoutPour(null); }
  };

  // ---- 3. Compte rendu (RP30) -------------------------------------------------
  const nbRevues = (reunion?.revue_actions || []).length;
  const nbPointes = (reunion?.invites || []).filter((i) => i.reponse && i.reponse !== 'en_attente').length;

  const genererCR = async () => {
    const res = await appel('cr', () => axios.post(`${API_URL}/api/chantiers/${chantierId}/reunions/${eid}/compte-rendu`, {
      notes, sources,
      prochaine_reunion: prochaineDate ? { date: prochaineDate.slice(0, 10), heure: prochaineDate.slice(11, 16), lieu: reunion?.lieu || '' } : null,
    }, { ...opts, timeout: 120000 }));
    if (!res) return;
    // A2 (T1) — pas de mention technique : un texte préparé sans l'Assistant est « à compléter »
    if (res.data?.compte_rendu?.redaction_incomplete) {
      toast.warning("Compte rendu préparé avec vos notes, les présents et les actions : complétez les parties « À compléter » avant la diffusion.");
    } else {
      toast.success("Compte rendu rédigé — relisez-le");
    }
  };

  const enregistrerTexte = async () => {
    const champ = cr?.rapport_ia ? 'rapport_ia' : 'contenu_ia';
    const res = await appel('texte', () => axios.patch(`${API_URL}/api/chantiers/${chantierId}/crc/${cr.id}`, { [champ]: texte }, opts), 'Compte rendu enregistré');
    if (res) setEdition(false);
  };

  // ---- 4. Relecture, diffusion, réunion suivante (RP31) -------------------------
  const planifierProchaine = async (silencieux = false) => {
    const res = await appel('prochaine', () => axios.post(`${API_URL}/api/chantiers/${chantierId}/reunions/${eid}/prochaine`, {
      date_debut: prochaineDate || null,
    }, opts), silencieux ? null : 'Réunion suivante planifiée');
    return res;
  };

  const validerEtDiffuser = async () => {
    if (edition) await enregistrerTexte();
    await planifierProchaine(true); // « prochaine réunion » annoncée dans l'e-mail de diffusion
    setDiffuser(true);
  };

  const soumettre = async () => {
    if (edition) await enregistrerTexte();
    await planifierProchaine(true);
    await appel('soumettre', () => axios.post(`${API_URL}/api/chantiers/${chantierId}/crc/${cr.id}/soumettre`, {}, opts), "Compte rendu soumis à l'architecte");
  };

  const statutCR = cr?.statut || 'brouillon';
  const crDiffuse = statutCR === 'diffuse' || statutCR === 'signe';

  const groupes = etat?.actions || [];
  const nbActions = useMemo(() => groupes.reduce((n, g) => n + g.actions.length, 0), [groupes]);

  return (
    <div className="fixed inset-0 z-40 bg-[#F5F5F3] overflow-y-auto" data-testid="reunion-semaine">
      <div className="sticky top-0 z-10 bg-white border-b border-[#E0E0DB] px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl text-[#2C2C2C] truncate" style={{ fontFamily: 'DM Serif Display, serif' }}>{FONCTIONS.reunionSemaine}</h2>
          {reunion && (
            <p className="text-xs text-[#6B6B6B] truncate" data-testid="reunion-semaine-entete">
              {reunion.titre} — {dateFr(reunion.date_debut)} {String(reunion.date_debut || '').slice(11, 16)}{reunion.lieu ? ` — ${reunion.lieu}` : ''}
              {String(reunion.date_debut || '').slice(0, 10) > new Date().toISOString().slice(0, 10) ? ' · à venir' : ''}
              {reunion.convocation_envoyee ? ` · convoquée${reunion.convocation_envoyee_at ? ` le ${dateFr(reunion.convocation_envoyee_at)}` : ''}` : ''}
            </p>
          )}
        </div>
        <button type="button" onClick={onClose} className={`${BTN} border-[#E0E0DB] bg-white`} aria-label="Fermer" data-testid="reunion-semaine-fermer">
          <X size={20} />
        </button>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {loading && <p className="text-sm text-[#6B6B6B]">Chargement…</p>}

        {!loading && !reunion && (
          <div className="card-kroquis text-center space-y-3" data-testid="reunion-semaine-aucune">
            {/* A2 (constat 25) — la réunion du jour ou la prochaine planifiée s'ouvre d'elle-même ;
                on n'arrive ici que si aucune réunion n'est planifiée. */}
            <p className="text-sm text-[#2C2C2C]">Aucune réunion n'est planifiée sur ce chantier.</p>
            {etat?.derniere_reunion && (
              <p className="text-xs text-[#6B6B6B]">
                La réunion d'aujourd'hui sera créée à partir de celle du {dateFr(etat.derniere_reunion.date_debut)} : mêmes participants, même lieu, même heure.
              </p>
            )}
            <button type="button" onClick={ouvrir} disabled={busy === 'ouvrir'} className={`${BTN} bg-[#C4773B] text-white border-[#C4773B]`} data-testid="reunion-semaine-ouvrir">
              <CalendarPlus size={18} /> Tenir une réunion aujourd'hui
            </button>
          </div>
        )}

        {reunion && (
          <>
            {/* 1. Présence */}
            <Etape numero={1} titre="Présence" icon={Users} testid="reunion-etape-presence">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <button type="button" onClick={tousPresents} disabled={!!busy} className={`${BTN} bg-[#2D6A4F] text-white border-[#2D6A4F]`} data-testid="reunion-tous-presents">
                  <CheckCircle size={18} /> Tous présents
                </button>
                <span className="text-xs text-[#6B6B6B]">puis touchez les exceptions</span>
              </div>
              {(reunion.invites || []).length === 0 && (
                <p className="text-sm text-[#6B6B6B]">Aucun participant n'est inscrit à cette réunion.</p>
              )}
              <ul className="divide-y divide-[#E0E0DB]">
                {(reunion.invites || []).map((inv, i) => (
                  <li key={`${inv.email || inv.nom}-${i}`} className="py-2 flex flex-wrap items-center gap-2" data-testid={`reunion-participant-${i}`}>
                    <div className="flex-1 min-w-[160px]">
                      <p className="text-sm font-medium text-[#2C2C2C]">{inv.nom || inv.email}</p>
                      {inv.role && <p className="text-xs text-[#6B6B6B]">{inv.role}</p>}
                    </div>
                    <div className="flex gap-1">
                      {PRESENCES.map((p) => (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => pointer(i, p.value)}
                          disabled={!!busy}
                          className={`${BTN} ${inv.reponse === p.value ? p.on : 'bg-white border-[#E0E0DB] text-[#2C2C2C]'}`}
                          aria-pressed={inv.reponse === p.value}
                          data-testid={`reunion-presence-${i}-${p.value}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </Etape>

            {/* 2. Revue des actions */}
            <Etape numero={2} titre={`${OBJETS.actions} ouvertes (${nbActions})`} icon={ListChecks} testid="reunion-etape-actions">
              {groupes.length === 0 && <p className="text-sm text-[#6B6B6B]">Aucune action ouverte sur ce chantier.</p>}
              <div className="space-y-4">
                {groupes.map((g) => (
                  <div key={g.entreprise} data-testid={`reunion-groupe-${g.entreprise}`}>
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-semibold text-[#1E3A5F]">{g.entreprise} <span className="text-[#6B6B6B] font-normal">({g.actions.length})</span></h4>
                      <button type="button" onClick={() => { setAjoutPour(g.entreprise); setAjout({ titre: '', entreprise: g.entreprise === 'Sans entreprise' ? '' : g.entreprise, echeance: dansJours(7) }); }} className={`${BTN} bg-white border-[#E0E0DB]`} data-testid={`reunion-ajout-${g.entreprise}`}>
                        <Plus size={16} /> {OBJETS.action}
                      </button>
                    </div>
                    <ul className="divide-y divide-[#E0E0DB] border border-[#E0E0DB] bg-white">
                      {g.actions.map((a) => {
                        const decision = a.revue && DECISION_LABEL[a.revue.decision] ? DECISION_LABEL[a.revue.decision](a.revue) : null;
                        const levee = a.revue?.decision === 'levee' || a.etat === 'fait';
                        return (
                          <li key={a.id} className="p-2 flex flex-wrap items-center gap-2" data-testid={`reunion-action-${a.id}`}>
                            <div className="flex-1 min-w-[200px]">
                              <p className={`text-sm ${levee ? 'line-through text-[#6B6B6B]' : 'text-[#2C2C2C]'}`}>{a.titre}</p>
                              <p className="text-xs text-[#6B6B6B]">
                                {a.lot ? `${a.lot} · ` : ''}
                                {a.echeance ? <span className={a.en_retard && !levee ? 'text-[#8B3A3A] font-semibold' : ''}>Échéance {dateFr(a.echeance)}{a.en_retard && !levee ? ' (dépassée)' : ''}</span> : 'Sans échéance'}
                                {a.nb_crc_suspens > 1 ? ` · reprise depuis ${a.nb_crc_suspens} CR` : ''}
                                {' · '}{STATUT_ACTION[a.etat] || a.etat}
                              </p>
                              {a.etat === 'a_verifier' && a.preuve?.commentaire && (
                                <p className="text-xs text-[#2D6A4F]">Preuve de l'entreprise : {a.preuve.commentaire}</p>
                              )}
                              {(a.desaccords || []).map((dz, k) => (
                                <p key={k} className="text-xs text-[#8B3A3A]" data-testid={`reunion-desaccord-${a.id}`}>
                                  Désaccord{dz.entreprise || dz.auteur ? ` de ${dz.entreprise || dz.auteur}` : ''}{dz.crc_numero ? ` (CR n° ${dz.crc_numero})` : ''} : {dz.commentaire}
                                </p>
                              ))}
                              {decision && <span className="inline-block mt-1 text-[11px] px-1.5 py-0.5 bg-[#F5F5F3] border border-[#E0E0DB]" data-testid={`reunion-decision-${a.id}`}>{decision}</span>}
                            </div>
                            {!levee && (
                              <div className="flex flex-wrap gap-1">
                                {reportId === a.id ? (
                                  <>
                                    <input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} className="min-h-[44px] px-2 border border-[#E0E0DB] text-sm" data-testid={`reunion-report-date-${a.id}`} />
                                    <button type="button" onClick={() => reporter(a, reportDate)} disabled={!reportDate || !!busy} className={`${BTN} bg-[#1E3A5F] text-white border-[#1E3A5F]`} data-testid={`reunion-report-ok-${a.id}`}>OK</button>
                                  </>
                                ) : (
                                  <button type="button" onClick={() => { setReportId(a.id); setReportDate(dansJours(7, a.echeance && !a.en_retard ? a.echeance : null)); }} disabled={!!busy} className={`${BTN} bg-white border-[#E0E0DB]`} data-testid={`reunion-reporter-${a.id}`}>
                                    <ArrowClockwise size={16} /> Reporter
                                  </button>
                                )}
                                {droits.peut_lever && (
                                  <button type="button" onClick={() => lever(a)} disabled={!!busy} className={`${BTN} bg-[#2D6A4F] text-white border-[#2D6A4F]`} data-testid={`reunion-lever-${a.id}`}>
                                    <CheckCircle size={16} /> {a.etat === 'a_verifier' ? BOUTONS.validerLevee : 'Lever'}
                                  </button>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
              {!droits.peut_lever && nbActions > 0 && (
                <p className="text-xs text-[#6B6B6B] mt-2">La levée d'une action est validée par l'architecte.</p>
              )}
              {ajoutPour === null ? (
                <button type="button" onClick={() => { setAjoutPour(''); setAjout({ titre: '', entreprise: '', echeance: dansJours(7) }); }} className={`${BTN} mt-3 bg-white border-[#E0E0DB]`} data-testid="reunion-ajout-action">
                  <Plus size={16} /> Nouvelle {OBJETS.action.toLowerCase()}
                </button>
              ) : (
                <div className="mt-3 p-3 border border-[#C4773B] bg-[#FDF6EC] grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="reunion-ajout-form">
                  <input value={ajout.titre} onChange={(e) => setAjout({ ...ajout, titre: e.target.value })} placeholder="Ce qui doit être fait" maxLength={200} className="sm:col-span-2 min-h-[44px] px-2 border border-[#E0E0DB] text-sm" data-testid="reunion-ajout-titre" />
                  <input value={ajout.entreprise} onChange={(e) => setAjout({ ...ajout, entreprise: e.target.value })} placeholder="Entreprise" list="reunion-entreprises" className="min-h-[44px] px-2 border border-[#E0E0DB] text-sm" data-testid="reunion-ajout-entreprise" />
                  <datalist id="reunion-entreprises">
                    {groupes.filter((g) => g.entreprise !== 'Sans entreprise').map((g) => <option key={g.entreprise} value={g.entreprise} />)}
                  </datalist>
                  <input type="date" value={ajout.echeance} onChange={(e) => setAjout({ ...ajout, echeance: e.target.value })} className="min-h-[44px] px-2 border border-[#E0E0DB] text-sm" data-testid="reunion-ajout-echeance" />
                  <div className="sm:col-span-4 flex gap-2 justify-end">
                    <button type="button" onClick={() => setAjoutPour(null)} className={`${BTN} bg-white border-[#E0E0DB]`}>Annuler</button>
                    <button type="button" onClick={ajouter} disabled={!!busy} className={`${BTN} bg-[#C4773B] text-white border-[#C4773B]`} data-testid="reunion-ajout-valider">Ajouter</button>
                  </div>
                </div>
              )}
            </Etape>

            {/* 3. Compte rendu */}
            <Etape numero={3} titre={OBJETS.compteRendu} icon={Robot} testid="reunion-etape-cr">
              {!cr ? (
                <div className="space-y-3">
                  <p className="text-xs text-[#6B6B6B]">Sources reprises par l'Assistant (décochez ce qui ne doit pas figurer) :</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ['actions_revues', `${OBJETS.actions} revues en réunion (${nbRevues})`],
                      ['observations', `${OBJETS.observations} depuis le dernier CR (${etat?.depuis_dernier_cr?.nb_observations ?? 0})`],
                      ['presences', `Présences (${nbPointes}/${(reunion.invites || []).length} pointées)`],
                    ].map(([cle, label]) => (
                      <label key={cle} className="min-h-[44px] px-3 border border-[#E0E0DB] bg-white text-sm inline-flex items-center gap-2 cursor-pointer" data-testid={`reunion-source-${cle}`}>
                        <input type="checkbox" checked={sources[cle]} onChange={(e) => setSources({ ...sources, [cle]: e.target.checked })} className="w-5 h-5" />
                        {label}
                      </label>
                    ))}
                  </div>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Notes de réunion : décisions, consignes aux entreprises… (dictée possible)" className="w-full p-2 border border-[#E0E0DB] text-sm" data-testid="reunion-notes" />
                  <p className="text-xs text-[#6B6B6B]">
                    Période : depuis le {dateFr(etat?.depuis_dernier_cr?.date_debut)}
                    {etat?.depuis_dernier_cr?.dernier_cr_numero ? ` (${libelleCompteRendu(etat.depuis_dernier_cr.dernier_cr_numero)})` : ''}.
                  </p>
                  <button type="button" onClick={genererCR} disabled={!!busy} className={`${BTN} bg-[#7B4E9B] text-white border-[#7B4E9B]`} data-testid="reunion-generer-cr">
                    {busy === 'cr' ? <ArrowClockwise size={18} className="animate-spin" /> : <Robot size={18} weight="fill" />} Rédiger le compte rendu avec l'Assistant
                  </button>
                </div>
              ) : (
                <div className="space-y-2" data-testid="reunion-cr">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">{libelleCompteRendu(cr.numero)}</span>
                    <span className="text-[11px] px-1.5 py-0.5 border border-[#E0E0DB] bg-[#F5F5F3]" data-testid="reunion-cr-statut">{STATUT_COMPTE_RENDU[statutCR] || statutCR}</span>
                    {!crDiffuse && statutCR === 'brouillon' && !edition && (
                      <button type="button" onClick={() => setEdition(true)} className={`${BTN} bg-white border-[#E0E0DB] ml-auto`} data-testid="reunion-cr-modifier">
                        <PencilSimple size={16} /> {BOUTONS.modifierCompteRendu}
                      </button>
                    )}
                  </div>
                  {edition ? (
                    <>
                      <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={18} className="w-full p-2 border border-[#E0E0DB] font-mono text-xs" data-testid="reunion-cr-texte" />
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => { setTexte(cr.texte || ''); setEdition(false); }} className={`${BTN} bg-white border-[#E0E0DB]`}>Annuler</button>
                        <button type="button" onClick={enregistrerTexte} disabled={!!busy} className={`${BTN} bg-[#2C2C2C] text-white border-[#2C2C2C]`} data-testid="reunion-cr-enregistrer">Enregistrer</button>
                      </div>
                    </>
                  ) : (
                    <div className="border border-[#E0E0DB] bg-white p-3 max-h-[480px] overflow-y-auto prose prose-sm max-w-none">
                      <CRCMarkdownRenderer md={texte} photosMeta={cr.photos_meta} sourcesIndex={cr.sources_index} />
                    </div>
                  )}
                </div>
              )}
            </Etape>

            {/* 4. Relecture, diffusion, réunion suivante */}
            <Etape numero={4} titre="Relecture et diffusion" icon={PaperPlaneRight} testid="reunion-etape-diffusion">
              <div className="flex flex-wrap items-end gap-2 mb-3">
                <label className="text-sm">
                  <span className="block text-xs text-[#6B6B6B] mb-1">Prochaine réunion (proposée : +7 jours, même heure, mêmes participants)</span>
                  <input type="datetime-local" value={prochaineDate} onChange={(e) => setProchaineDate(e.target.value)} className="min-h-[44px] px-2 border border-[#E0E0DB] text-sm" data-testid="reunion-prochaine-date" />
                </label>
                <button type="button" onClick={() => planifierProchaine(false)} disabled={!!busy || !prochaineDate} className={`${BTN} bg-white border-[#E0E0DB]`} data-testid="reunion-planifier-prochaine">
                  <CalendarPlus size={16} /> {etat?.prochaine_reunion ? 'Modifier la date' : 'Planifier'}
                </button>
                {etat?.prochaine_reunion && (
                  <span className="text-xs text-[#2D6A4F]" data-testid="reunion-prochaine-planifiee">
                    Planifiée le {dateFr(etat.prochaine_reunion.date_debut)} à {String(etat.prochaine_reunion.date_debut || '').slice(11, 16)}
                  </span>
                )}
              </div>
              {!cr && <p className="text-sm text-[#6B6B6B]">Rédigez d'abord le compte rendu (étape 3).</p>}
              {cr && crDiffuse && (
                <p className="text-sm text-[#2D6A4F]" data-testid="reunion-cr-diffuse">Compte rendu diffusé — contenu verrouillé.</p>
              )}
              {cr && !crDiffuse && droits.peut_diffuser && (
                <button type="button" onClick={validerEtDiffuser} disabled={!!busy} className={`${BTN} bg-[#C4773B] text-white border-[#C4773B]`} data-testid="reunion-valider-diffuser">
                  <PaperPlaneRight size={18} weight="bold" /> {BOUTONS.validerEtDiffuser}
                </button>
              )}
              {cr && !crDiffuse && !droits.peut_diffuser && statutCR === 'brouillon' && (
                <button type="button" onClick={soumettre} disabled={!!busy} className={`${BTN} bg-[#1E3A5F] text-white border-[#1E3A5F]`} data-testid="reunion-soumettre">
                  <CheckCircle size={18} weight="bold" /> {BOUTONS.soumettreArchitecte}
                </button>
              )}
              {cr && !crDiffuse && !droits.peut_diffuser && statutCR !== 'brouillon' && (
                <p className="text-sm text-[#1E3A5F]" data-testid="reunion-en-relecture">
                  <Warning size={14} className="inline mr-1" />En relecture chez l'architecte.
                </p>
              )}
            </Etape>
          </>
        )}
      </div>

      {diffuser && cr && (
        <SendCRCDialog
          chantierId={chantierId}
          crc={{ ...cr, id: cr.id }}
          onClose={() => setDiffuser(false)}
          onSuccess={(res) => {
            setDiffuser(false);
            toast.success(`${OBJETS.compteRendu} diffusé à ${res?.sent_count || 0} destinataire(s)`);
            charger();
          }}
        />
      )}
    </div>
  );
}
